import { OrderStatus } from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Anchor,
  Avatar,
  Button,
  Checkbox,
  Grid,
  Group,
  Image,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { OrderAddressCard } from '../components/OrderAddressCard';
import { OrderCancellationsTable } from '../components/OrderCancellationsTable';
import { OrderCustomerCard } from '../components/OrderCustomerCard';
import { OrderDetailsHeader } from '../components/OrderDetailsHeader';
import { OrderLinesTable } from '../components/OrderLinesTable';
import {
  sendMessageDocument,
  setIbanTypeDocument,
  setPaymentMethodOnOrderDocument,
  setShippingMethodOnOrderDocument,
  updateOrderDocument,
} from '../graphql/mutations';
import { orderDocument } from '../graphql/queries';

const orderStatusTranslations = {
  [OrderStatus.Created]: 'Vytvorená',
  [OrderStatus.Confirmed]: 'Potvrdená',
  [OrderStatus.Processed]: 'Spracovaná',
  [OrderStatus.Shipped]: 'Odoslaná',
  [OrderStatus.Delivered]: 'Doručená',
  [OrderStatus.Cancelled]: 'Zrušená',
  [OrderStatus.Reviewing]: 'V riešení',
  [OrderStatus.Returned]: 'Vrátená',
  [OrderStatus.Internal]: 'Interná',
  [OrderStatus.Reserved]: 'Rezervovaná',
};

const Linkify = ({ text }: { text: string }) => {
  const renderTextWithLinks = () => {
    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Check if the URL points to an image
    const isImageUrl = (url: string) => {
      return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*|#.*)?$/i.test(url);
    };

    const parts = text.split(urlRegex).map((part, index) => {
      if (urlRegex.test(part)) {
        if (isImageUrl(part)) {
          return <Image key={index} src={part} w="70%" />;
        }

        return (
          <Anchor key={index} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </Anchor>
        );
      }

      return part;
    });

    return parts;
  };

  return <Text>{renderTextWithLinks()}</Text>;
};

export const OrderDetails = () => {
  const { orderId } = useParams();
  const viewport = useRef<HTMLDivElement>(null);

  const [message, setMessage] = useState('');

  const { data, loading } = useQuery(orderDocument, { variables: { where: { id: orderId } } });

  const [updateOrder] = useMutation(updateOrderDocument, {
    refetchQueries: 'all',
    awaitRefetchQueries: true,
  });

  const [setIbanTypeMutate] = useMutation(setIbanTypeDocument, {
    refetchQueries: 'all',
    awaitRefetchQueries: true,
  });

  const [setShippingMethodOnOrder, setShippingMethodOnOrderMutation] = useMutation(
    setShippingMethodOnOrderDocument,
    {
      refetchQueries: 'all',
      awaitRefetchQueries: true,
    }
  );

  const [setPaymentMethodOnOrder, setPaymentMethodOnOrderMutation] = useMutation(
    setPaymentMethodOnOrderDocument,
    {
      refetchQueries: 'all',
      awaitRefetchQueries: true,
    }
  );

  const [sendMessage, sendMessageMutation] = useMutation(sendMessageDocument, {
    refetchQueries: 'all',
    awaitRefetchQueries: true,
  });

  const [showEchoMessages, setShowEchoMessage] = useState(false);

  useEffect(() => {
    if (data?.order?.customer?.messages && data.order.customer.messages.length > 0)
      viewport.current?.scrollTo({ top: viewport.current!.scrollHeight });
  }, [data?.order?.customer.messages, showEchoMessages]);

  if (!orderId) return <Alert color="red">Order ID not set</Alert>;

  if (loading) return <Loader />;

  if (!data?.order) return <Alert color="red">Order not found</Alert>;

  return (
    <Grid>
      <Grid.Col>
        <OrderDetailsHeader order={data.order} />
      </Grid.Col>
      <Grid.Col span={8} component={Stack}>
        <OrderLinesTable orderId={data.order.id} lines={data.order.lines} />
        {data.order.cancellations.length > 0 && (
          <OrderCancellationsTable
            orderId={data.order.id}
            cancellations={data.order.cancellations}
          />
        )}
        <Paper withBorder p="md">
          <Checkbox
            checked={showEchoMessages}
            onChange={() => setShowEchoMessage(!showEchoMessages)}
            label="Zobraziť aj naše správy?"
          />
          <ScrollArea viewportRef={viewport} h={500}>
            <Stack gap="xs">
              {data.order.customer.messages
                ?.slice()
                .filter((message) => showEchoMessages || !message.isEcho)
                .reverse()
                .map((message) => (
                  <Group
                    key={message.id}
                    maw="70%"
                    style={{
                      alignSelf: message.isEcho ? 'flex-end' : 'flex-start',
                      flexDirection: message.isEcho ? 'row-reverse' : 'row',
                    }}
                  >
                    <Avatar
                      src={!message.isEcho ? message.customer.avatar : undefined}
                      name={message.isEcho ? 'Lásky Krásky' : message.customer.name}
                    />
                    <Paper
                      key={message.id}
                      p="sm"
                      bg={message.isEcho ? 'dark' : 'gray'}
                      radius="md"
                      component={Stack}
                      gap={0}
                    >
                      <Linkify text={message.body} />
                      <Text
                        size="sm"
                        c="dimmed"
                        style={{
                          alignSelf: message.isEcho ? 'flex-end' : 'flex-start',
                        }}
                      >
                        {dayjs(message.createdAt).format('DD.MM.YYYY HH:mm:ss')}
                      </Text>
                    </Paper>
                  </Group>
                ))}
            </Stack>
          </ScrollArea>
          <TextInput
            mr="md"
            placeholder='Napíšte správu a stlačte "Enter"'
            value={message}
            disabled={sendMessageMutation.loading}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;

              void sendMessage({
                variables: { orderId, message },
              });

              setMessage('');
            }}
          />
          <Group mt="md">
            <Button
              size="compact-sm"
              onClick={async () => {
                await sendMessage({
                  variables: { orderId, message: `Čakáme na úhradu inak bude objednávka zrušená.` },
                });
              }}
            >
              Poslať čakáme na úhradu
            </Button>
            <Button
              size="compact-sm"
              onClick={async () => {
                await sendMessage({
                  variables: { orderId, message: `SK98 5600 0000 0062 5205 0001` },
                });

                await setIbanTypeMutate({
                  variables: { orderId, ibanType: 'F' },
                });
              }}
            >
              Poslať IBAN (SK)
            </Button>
            <Button
              size="compact-sm"
              onClick={async () => {
                await sendMessage({
                  variables: { orderId, message: `LT21 3250 0932 9926 0735` },
                });

                await setIbanTypeMutate({
                  variables: { orderId, ibanType: 'LT' },
                });
              }}
            >
              Poslať IBAN (LT)
            </Button>
            <Button
              size="compact-sm"
              onClick={async () => {
                await sendMessage({
                  variables: {
                    orderId,
                    message: `Spracovali sme vašu reklamáciu. Peniaze vám budú vrátené do 14 dní na účet.`,
                  },
                });
              }}
            >
              Prijatá reklamácia
            </Button>
          </Group>
        </Paper>
      </Grid.Col>
      <Grid.Col span={4} component={Stack}>
        <OrderCustomerCard orderId={data.order.id} customer={data.order.customer} />
        <OrderAddressCard
          orderId={data.order.id}
          address={data.order.address}
          customer={data.order.customer}
        />
        {data.order.paymentMethod?.name === 'Prevod' && (
          <Paper p="md" withBorder>
            <Text fw={500} size="lg">
              IBAN
            </Text>
            {!data.order.customer.metadata?.ibanType && (
              <Alert color="orange">Nastav na aký IBAN platí!</Alert>
            )}
            <Select
              data={['LT', 'F']}
              value={data.order.customer.metadata?.ibanType}
              onChange={async (value) => {
                if (!data.order) return;

                await setIbanTypeMutate({
                  variables: { orderId: data.order.id, ibanType: value ?? '' },
                });
              }}
              placeholder="IBAN"
              allowDeselect={false}
            />
          </Paper>
        )}

        <Paper p="md" withBorder>
          <Select
            label="Spôsob dopravy"
            data={[
              {
                value: 'cm4bow5ql0005n82snqsojj6y',
                label: 'Osobný odber',
              },
              { value: 'cm4bow5ql0000n82snszbcu35', label: 'DPD SK 3 €' },
              { value: 'cm4bow5ql0001n82s3vzki3mw', label: 'DPD CZ 6 €' },
              {
                value: 'cm4bow5ql0002n82s75d1eayz',
                label: 'DPD AT 6 €',
              },
              {
                value: 'cm4bow5ql0003n82sxy5rvxat',
                label: 'DPD HU 6 €',
              },
              {
                value: 'cm4bow5ql0004n82s1zkljx8m',
                label: 'DPD DE 12 €',
              },
            ]}
            value={data.order.shippingMethod?.id}
            onChange={(value) => {
              if (!value) return;

              void setShippingMethodOnOrder({ variables: { orderId, shippingMethodId: value } });
            }}
            disabled={setShippingMethodOnOrderMutation.loading}
          />
          <Select
            label="Spôsob platby"
            data={[
              { value: 'cm4bow5qp0006n82sw9p0xhfr', label: 'Prevod' },
              { value: 'cm4bow5qp0007n82sev08mt7e', label: 'Dobierka 3 €' },
              { value: 'cm4bow5qp0008n82sx4ckil15', label: 'Karta (Link)' },
              { value: 'cm4bow5qp0009n82su9fyiisa', label: 'Osobne' },
            ]}
            value={data.order.paymentMethod?.id}
            onChange={(value) => {
              if (!value) return;

              void setPaymentMethodOnOrder({ variables: { orderId, paymentMethodId: value } });
            }}
            disabled={setPaymentMethodOnOrderMutation.loading}
          />
          <Select
            allowDeselect={false}
            label="Stav objednávky"
            data={Object.values(OrderStatus).map((status) => ({
              value: status,
              label: orderStatusTranslations[status],
            }))}
            value={data.order.status}
            onChange={(value) => {
              if (!value) return;

              void updateOrder({
                variables: {
                  where: { id: orderId },
                  data: { status: { set: value as OrderStatus } },
                },
              });
            }}
          />
        </Paper>
      </Grid.Col>
    </Grid>
  );
};
